import { Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { hasAnyAccessToResource } from "api/general";
import { useAppSelector } from "app/hooks";
import { ReactComponent as ChevronUpIcon } from "components/Icons/chevron-up.svg";
import { ReactComponent as Trash02Icon } from "components/Icons/trash-02.svg";
import {
  Condition,
  ConditionError,
} from "components/Layout/Campaign/Common/CampaignRules/Rule/Conditions/Condition";
import {
  CommandBar,
  ConditionWithCounter,
} from "components/Tooltips/CommandBar/CommandBar";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import { Fragment, useState } from "react";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import {
  CampaignCondition,
  CampaignConditionType,
  SelectedSchemaCondition,
} from "types/campaigns";
import { alphabetizeByProperty } from "utilities/arrays";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    conditionsWrapper: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      border: `1px solid ${theme.gray200}`,
      borderRadius: "8px",
      overflow: "visible",
      margin: theme.spacing(0, 0, 8, 0),
    },
    topFunctions: {
      alignItems: "center",
      background: theme.gray50,
      borderBottom: `1px solid ${theme.gray200}`,
      borderRadius: "8px 8px 0 0",
      display: "flex",
      gap: theme.spacing(8),
      justifyContent: "end",
      padding: theme.spacing(6),
    },
    topFunctionsHidden: {
      borderBottom: "none",
      borderRadius: "8px",
    },
    bigIcon: {
      color: theme.gray500,
      cursor: "pointer",
      transition: "0.2s",
    },
    iconReverted: { transform: "rotate(180deg)" },
    conditionsHeaderContainer: {
      alignItems: "baseline",
      display: "flex",
      justifyContent: "space-between",
      margin: theme.spacing(8),
    },
    conditionHeader: {
      fontSize: 14,
      fontWeight: 500,
      marginBottom: theme.spacing(6),
    },
    conditionWarning: {
      fontSize: 14,
      color: theme.red500,
      marginLeft: theme.spacing(8),
      marginBottom: 0,
    },
  }),
);

interface Props {
  errors: any;
  activeRule: number;
  conditions: (CampaignCondition | SelectedSchemaCondition)[];
}

export const RuleConditions = ({ errors, activeRule, conditions }: Props) => {
  const { message } = useMessage();
  const classes = useStyles();
  const { currency } = useAppSelector(selectCurrentStoreData);
  const defaultWallet = useAppSelector(selectDefaultWalletData);
  const hasAnyTierPermission = hasAnyAccessToResource("LEVEL");
  const hasAnyChannelPermission = hasAnyAccessToResource("CHANNEL");
  const [isOpen, setOpen] = useState(true);
  const [isCommandBarOpen, setIsCommandBarOpen] = useState(false);

  const defaultPopularConditions: CampaignCondition[] = [
    {
      attribute: "customer.segments",
      label: "memberSegments",
      type: "segmentDropdown",
    },
    {
      attribute: "transaction.grossValue",
      label: "transactionValueWithCurrency",
      type: "float",
    },
    {
      attribute: "transaction.category.qty",
      label: "transactionCategoryQty",
      type: "int",
    },
    {
      attribute: "transaction.category.grossValue",
      label: "transactionCategoryGrossValue",
      type: "float",
    },
    {
      attribute: "expression",
      label: "expression",
      operator: "expression",
      type: "expression",
    },
  ];

  return (
    <FieldArray
      name={`rules.[${activeRule}].conditions`}
      render={({
        remove,
        replace,
        insert,
        unshift,
        form: { values, setFieldValue, setFieldError },
      }) => {
        const { rules } = values;

        const areConditionsAdded =
          rules && rules[activeRule].conditions?.length > 0;

        const conditionsList = alphabetizeByProperty(
          [
            ...(!hasAnyChannelPermission
              ? conditions.filter(
                  (condition) =>
                    condition.attribute !== "transaction.channelId",
                )
              : !hasAnyTierPermission
                ? conditions.filter(
                    (condition) =>
                      condition.attribute !== "customer.levelId" &&
                      condition.attribute !== "customer.tiers",
                  )
                : conditions
            ).map((item) => ({
              ...item,
              label: message(
                {
                  id: item.label,
                },
                {
                  walletName: defaultWallet?.name,
                  units: defaultWallet?.unitPluralName,
                  currency,
                },
              ),
            })),
          ],
          "label",
        );

        return (
          <>
            {areConditionsAdded ? (
              <div className={classes.conditionsWrapper}>
                <div
                  className={`${classes.topFunctions} ${
                    isOpen ? "" : classes.topFunctionsHidden
                  }`}
                >
                  <Trash02Icon
                    className={classes.bigIcon}
                    data-test-id="achievement-delete"
                    onClick={() => {
                      setFieldValue(`rules[${activeRule}].conditions`, []);
                    }}
                  />
                  <ChevronUpIcon
                    onClick={() => setOpen(!isOpen)}
                    className={`${classes.bigIcon} ${
                      isOpen ? "" : classes.iconReverted
                    }`}
                    fontSize="small"
                    data-test-id="achievement-open-details"
                  />
                </div>
                {isOpen && (
                  <>
                    <div className={classes.conditionsHeaderContainer}>
                      <span className={classes.conditionHeader}>
                        {message({ id: "conditions" })}
                      </span>
                      <OLButton
                        data-test-id={`rule-${activeRule}-condition-add-button`}
                        onClick={() => setIsCommandBarOpen(true)}
                        variant="outlined"
                      >
                        {message(
                          { id: "plusMore" },
                          { more: message({ id: "addCondition" }) },
                        )}
                      </OLButton>
                    </div>
                    {rules[activeRule]?.conditions?.map(
                      (
                        input: {
                          isEditing: boolean;
                          isSaved: boolean;
                          attribute: string;
                          type?: CampaignConditionType;
                          draft?: {
                            attribute: string;
                            type?: CampaignConditionType;
                          };
                        },
                        index: number,
                      ) => {
                        const conditionsError =
                          errors && (errors as any).conditions;

                        const conditionError =
                          conditionsError && conditionsError[index];

                        const isConditionNotSaved =
                          conditionError && conditionError.isEditing;
                        return (
                          <Fragment key={index}>
                            {isConditionNotSaved && (
                              <Typography className={classes.conditionWarning}>
                                {message({ id: "notSavedConditionWarning" })}
                              </Typography>
                            )}
                            <Condition
                              condition={rules[activeRule]?.conditions[index]}
                              conditions={conditions}
                              data-test-id={`rule-${activeRule}-condition-${index}`}
                              error={conditionError as ConditionError}
                              index={index}
                              input={input.draft || input}
                              insert={insert}
                              isAchievementForm
                              remove={remove}
                              replace={replace}
                              rule={rules?.[activeRule]}
                              ruleIndex={activeRule}
                              setFieldError={setFieldError}
                              setFieldValue={setFieldValue}
                            />
                          </Fragment>
                        );
                      },
                    )}
                  </>
                )}
              </div>
            ) : (
              <OLButton
                data-test-id={`rule-${activeRule}-condition-add-button`}
                onClick={() => setIsCommandBarOpen(true)}
                variant="contained"
              >
                {message({ id: "addCondition" })}
              </OLButton>
            )}
            <CommandBar
              conditions={conditionsList}
              defaultPopularConditions={defaultPopularConditions}
              isOpen={isCommandBarOpen}
              onClose={() => setIsCommandBarOpen(false)}
              onItemClick={(item: ConditionWithCounter) => {
                unshift({
                  draft: item,
                  isEditing: true,
                  isSaved: false,
                });
              }}
              storagedConditionsName="achievementPopularConditions"
            />
          </>
        );
      }}
    />
  );
};
